import { useState, useEffect, useRef, useCallback, type CSSProperties } from 'react';

interface PullIndicatorProps {
  style: CSSProperties;
  'data-refreshing': boolean;
  'data-pulling': boolean;
}

interface PullToRefreshReturn {
  isRefreshing: boolean;
  pullIndicatorProps: PullIndicatorProps;
}

/**
 * usePullToRefresh - Pull-to-refresh gesture for mobile PWA
 * Attaches to the given scroll container and triggers a full page reload.
 */
export default function usePullToRefresh(
  containerSelector = '.content-area',
): PullToRefreshReturn {
  const threshold = 80;
  const maxPull = 120;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);

  const startY = useRef(0);

  const isAtTop = useCallback((): boolean => {
    // Never trigger pull-to-refresh when a modal is open
    if (document.querySelector('.modal-overlay, .modal-backdrop, [data-modal-open]')) return false;
    const container = document.querySelector(containerSelector);
    if (!container) return false;
    return container.scrollTop <= 0;
  }, [containerSelector]);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (isRefreshing) return;
    if (!isAtTop()) return;
    startY.current = e.touches[0]!.clientY;
    setIsPulling(true);
  }, [isRefreshing, isAtTop]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPulling || isRefreshing) return;
    if (!isAtTop()) {
      setPullDistance(0);
      return;
    }

    const diff = e.touches[0]!.clientY - startY.current;
    if (diff > 0) {
      const distance = Math.min(diff * 0.5, maxPull);
      setPullDistance(distance);
      if (distance > 10) e.preventDefault();
    }
  }, [isPulling, isRefreshing, isAtTop]);

  const handleTouchEnd = useCallback(() => {
    if (!isPulling) return;
    setIsPulling(false);

    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(threshold);
      // Reload the page after a brief visual pause
      setTimeout(() => window.location.reload(), 300);
    } else {
      setPullDistance(0);
    }
    startY.current = 0;
  }, [isPulling, pullDistance, isRefreshing]);

  useEffect(() => {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart as EventListener, { passive: true });
    container.addEventListener('touchmove', handleTouchMove as EventListener, { passive: false });
    container.addEventListener('touchend', handleTouchEnd as EventListener, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart as EventListener);
      container.removeEventListener('touchmove', handleTouchMove as EventListener);
      container.removeEventListener('touchend', handleTouchEnd as EventListener);
    };
  }, [containerSelector, handleTouchStart, handleTouchMove, handleTouchEnd]);

  const pullProgress = Math.min(pullDistance / threshold, 1);

  return {
    isRefreshing,
    pullIndicatorProps: {
      style: {
        transform: `translateY(${pullDistance - 60}px)`,
        opacity: pullProgress,
      },
      'data-refreshing': isRefreshing,
      'data-pulling': isPulling && pullDistance > 0,
    },
  };
}
